import { create } from 'zustand';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';

interface User {
  cognitoSub: string;
  email: string;
  isAdmin: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isCheckingAuth: boolean;
  isLoading: boolean;
  error: string | null;
  checkAuth: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isCheckingAuth: true,
  isLoading: false,
  error: null,

  checkAuth: async () => {
    try {
      set({ isCheckingAuth: true });
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
      const isAdmin = groups.includes('admins');

      set({
        user: {
          cognitoSub: currentUser.userId,
          email: currentUser.signInDetails?.loginId || '',
          isAdmin,
        },
        isAuthenticated: true,
        isCheckingAuth: false,
      });
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isCheckingAuth: false,
      });
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      // Clear any existing session first to avoid "user already signed in" error
      try {
        await amplifySignOut();
      } catch {
        // Ignore sign out errors - there may not be an active session
      }

      await amplifySignIn({ username: email, password });

      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
      const isAdmin = groups.includes('admins');

      set({
        user: {
          cognitoSub: currentUser.userId,
          email,
          isAdmin,
        },
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign in failed',
        isLoading: false,
      });
      throw error;
    }
  },

  signUp: async (email: string, password: string, displayName: string) => {
    try {
      set({ isLoading: true, error: null });
      await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            'custom:displayName': displayName,
          },
        },
      });
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign up failed',
        isLoading: false,
      });
      throw error;
    }
  },

  confirmSignUp: async (email: string, code: string) => {
    try {
      set({ isLoading: true, error: null });
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Confirmation failed',
        isLoading: false,
      });
      throw error;
    }
  },

  resendCode: async (email: string) => {
    try {
      set({ isLoading: true, error: null });
      await amplifyResendSignUpCode({ username: email });
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to resend code',
        isLoading: false,
      });
      throw error;
    }
  },

  signOut: async () => {
    try {
      await amplifySignOut();
      set({
        user: null,
        isAuthenticated: false,
      });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  },

  clearError: () => set({ error: null }),
}));
